/** @jsxImportSource @emotion/react */
import { useRef } from 'react'
import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import scheduleActions from '@sportninja/common/actions/schedule'
import orgActions from '@sportninja/common/actions/org'

import SearchBox from '../SearchBox'
import FormSheet from '../Form/FormSheet'
import { venueForm } from '../Form/form-configs'
import { connect } from 'react-redux'
// import { FormButton, inputCss, InputWrapper, Label } from '../Form/css'
import { Flex } from '../Layout'
import Icon from '../Icon'
import { NewModal } from '../NewModal'

const VenueFacilityPicker = ({
  createVenue,
  createVenueUnderOrg,
  defaultValues = {},
  onChange,
  ...props
}) => {
  const { facilityId, location = {}, parentId } = defaultValues
  const { id: locationId } = location
  const { organization_id, organization_name: organizationName = '' } = props

  const initialLoad = useRef(true)
  const [venues, setVenues] = useState([])
  const [selectedVenue, setSelectedVenue] = useState(locationId || '')
  const [facilities, setFacilities] = useState([])
  const [selectedFacility, setSelectedFacility] = useState(facilityId || '')
  const [showAllLocations, setShowAllLocations] = useState(true)
  const [loadingVenues, setLoadingVenues] = useState(false)

  useEffect(() => {
    setSelectedVenue(locationId)
  }, [locationId])

  useEffect(() => {
    setSelectedFacility(facilityId)
  }, [facilityId])

  useEffect(() => {
    setLoadingVenues(true)
    const url =
      typeof parentId !== 'string'
        ? '/venues'
        : (() => {
            const params = new URLSearchParams({ schedule_id: parentId })
            if (!showAllLocations) {
              params.set('recursive', '0')
            }
            return `/venues?${params.toString()}`
          })()
    req(url)
      .then((response) => {
        setVenues(response.data)
        initialLoad.current = false
      })
      .finally(() => {
        setLoadingVenues(false)
      })
  }, [parentId, showAllLocations])

  useEffect(() => {
    if (selectedVenue && selectedVenue.length > 0) {
      req(`/venues/${selectedVenue}/facilities`).then((response) => {
        setFacilities(response.data)
      })
    } else {
      setFacilities([])
      setSelectedFacility('')
      if (!initialLoad) {
        onChange({ target: { name: 'facility_id', value: undefined } })
      }
    }
  }, [selectedVenue])

  useEffect(() => {
    if (selectedVenue) {
      const selectedVenueTimezone = venues.find(
        (v) => v.id === selectedVenue
      )?.timezone

      if (selectedVenueTimezone) {
        onChange({
          target: { name: 'location_timezone', value: selectedVenueTimezone },
        })
      }
    }
  }, [selectedVenue, venues])

  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <FormSheet
        hideButton
        isOpen={isOpen}
        form={venueForm({}, parentId, ENTITY_TYPES.schedule)}
        label={t('Web:createLoc')}
        onCancel={() => setIsOpen(false)}
        onSubmit={async (...args) => {
          let response
          if (organization_id) {
            response = await createVenueUnderOrg(organization_id, ...args)
          } else {
            response = await createVenue(parentId, ...args)
          }
          setTimeout(() => {
            req(
              `/venues${
                typeof parentId === 'string' && `?schedule_id=${parentId}`
              }`
            ).then((response) => {
              const value = response.data[response.data.length - 1].id
              setSelectedVenue(value)
              setVenues(response.data)
              onChange({ target: { name: 'venue_id', value } })
              setIsOpen(false)
            })
          }, 300)

          return response
        }}
        title={t('Web:createANewLocation')}
      />
      <SearchBox
        topContent={
          typeof parentId === 'string' ? (
            <Flex
              className='item'
              alignItems='center'
              justifyContent='space-between'
              css={css`
                padding: 10px 12px;
                cursor: pointer;
                border: 1px solid white;
                background-color: rgba(24, 26, 29, 1);
              `}
              onMouseDown={(e) => {
                e.preventDefault()
                setShowAllLocations((v) => !v)
              }}
            >
              <span
                css={css`
                  font-size: 13px;
                  color: ${colors.WHITE};
                `}
              >
                {showAllLocations
                  ? 'All Locations'
                  : `Within ${organizationName || 'Organization'}`}
              </span>
              <span
                css={css`
                  position: relative;
                  display: inline-block;
                  flex: none;
                  height: 18px;
                  width: 32px;
                  transition: background-color 0.1s, border 0.1s ease-in-out;
                  background-color: ${showAllLocations && colors.DEFAULT_FLAIR};
                  border: 2px solid
                    ${showAllLocations ? colors.DEFAULT_FLAIR : 'white'};
                  border-radius: 25px;
                  box-sizing: border-box;
                `}
              >
                <span
                  css={css`
                    position: absolute;
                    top: 50%;
                    left: 0;
                    display: block;
                    background-color: ${colors.WHITE};
                    height: 10px;
                    width: 10px;
                    border-radius: 50%;
                    transition: transform 0.1s ease-in-out;
                    transform: translate(${showAllLocations ? 16 : 2}px, -50%);
                  `}
                />
              </span>
            </Flex>
          ) : null
        }
        bottomContent={
          <Flex
            className='item'
            alignItems='center'
            justifyContent='space-between'
            onClick={() => {
              setIsOpen(true)
              // locationModalRef.current.openModal()
            }}
            css={css`
              padding: 12px;
              margin-top: 1px;
              height: 38px;
              cursor: pointer;
              border: 1px solid white;
              background-color: rgba(24, 26, 29, 1);
            `}
          >
            Create a new location
            <Icon
              fontSize={20}
              name='map-marker-plus'
              css={css`
                margin-left: 8px;
              `}
            />
          </Flex>
        }
        data={venues}
        defaultValue={selectedVenue}
        label={t('util:capitalize', { text: t('common:location') })}
        loading={loadingVenues}
        name='venue_id'
        onChange={(e) => {
          setSelectedVenue(e?.target?.value)
          onChange(e)
        }}
        placeholder={`${t('Web:selectALocation')}...`}
        required
        {...props}
      />

      <SearchBox
        data={facilities}
        defaultValue={selectedFacility}
        disabled={facilities.length === 0}
        displayString='name'
        label={t('util:capitalize', { text: t('common:facility') })}
        name='facility_id'
        onChange={onChange}
        placeholder={`${t('Web:selectAFacility')}...`}
      />
    </>
  )
}

const mapDispatchToProps = (dispatch) => {
  return {
    createVenue: bindActionToPromise(
      dispatch,
      scheduleActions.venues.create.request
    ),
    createVenueUnderOrg: bindActionToPromise(
      dispatch,
      orgActions.venues.create.request
    ),
  }
}

export default connect(null, mapDispatchToProps)(VenueFacilityPicker)
